import { useEffect, useState } from 'react';
import { Library, Sparkles } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  cn,
  useToast,
  type SelectOption,
} from '../../ui';
import {
  TASK_TYPES,
  blankTaskDraft,
  validateTaskDraft,
} from '../../features/tasks/taskModel';
import type { Task, TaskDraft, TaskType } from '../../features/tasks/types';
import type { Sprint } from '../../features/sprintboard/models/types';

export interface QuickCreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  /** Persists a new task's draft (room-scoped, from useTasks). */
  onCreate: (draft: TaskDraft) => Promise<Task>;
  /** Called after create with the new task + whether to estimate it now. */
  onCreated: (task: Task, estimateNow: boolean) => void;
  /**
   * Grounds the freshly-created task against the team knowledge base and
   * attaches the AI research to it (persisted on the task, shown in the task
   * page's Knowledge Base Research section). Optional — omit to hide the
   * "Research with Vector KB" option. Reuses {@link useTasks}.researchTask.
   */
  onResearch?: (task: Task) => Promise<Task>;
  /** The room's current sprint (for the Current Sprint | Backlog choice). */
  currentSprint: Sprint | null;
}

const TYPE_OPTIONS: SelectOption[] = TASK_TYPES.map((t) => ({
  value: t,
  label: t,
}));

type SprintChoice = 'current' | 'backlog';

/**
 * Quick-create: the fast path to a task. It asks for ONLY the essentials —
 * title, type, and whether it goes into the current sprint or the backlog — so
 * a task is created in seconds. Tasks are created UNASSIGNED (no owner): the
 * flow is sprint-first, assign-later — an owner is picked from the Sprint Board
 * card menu or the task page once the work is planned. An OPTIONAL description
 * can be added inline (kept optional so create stays fast), and an OPTIONAL
 * "Research with Vector KB" toggle grounds the new task (title + description)
 * against the team knowledge base and attaches the AI research to it. Other
 * fields (priority, links, blockers) are added later on the task page. An
 * optional "Estimate now" toggle opens the estimate flow immediately after
 * creation, delivering the "estimate a task while creating it" path directly.
 */
export function QuickCreateTaskModal({
  open,
  onClose,
  onCreate,
  onCreated,
  onResearch,
  currentSprint,
}: QuickCreateTaskModalProps) {
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<TaskType>('Task');
  const [sprintChoice, setSprintChoice] = useState<SprintChoice>('backlog');
  const [estimateNow, setEstimateNow] = useState(false);
  const [researchNow, setResearchNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [researching, setResearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setType('Task');
    setSprintChoice(currentSprint ? 'current' : 'backlog');
    setEstimateNow(false);
    setResearchNow(false);
    setResearching(false);
  }, [open, currentSprint]);

  const sprintOptions: SelectOption[] = [
    ...(currentSprint
      ? [{ value: 'current', label: `Current Sprint · ${currentSprint.name}` }]
      : []),
    { value: 'backlog', label: 'Backlog' },
  ];

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async () => {
    const toCurrent = sprintChoice === 'current' && !!currentSprint;
    const draft: TaskDraft = {
      ...blankTaskDraft(toCurrent ? 'TODO' : 'BACKLOG'),
      title: title.trim(),
      // Optional multiline description — persisted on create; empty stays ''.
      description: description.trim(),
      type,
      // Sprint-first, assign-later: created UNASSIGNED (owner/assignee empty).
      // An owner is set from the Sprint Board card menu or the task page.
      sprintId: toCurrent ? currentSprint!.id : null,
      boardStatus: 'TODO',
    };

    const problems = validateTaskDraft(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot create task', description: problems[0] });
      return;
    }

    const wantResearch = researchNow && !!onResearch;

    setSaving(true);
    try {
      const created = await onCreate(draft);
      toast({ tone: 'success', title: 'Task created', description: created.title });

      // Opt-in KB research: best-effort and NON-BLOCKING of creation — the task
      // is already saved, so a KB/model failure only skips the attached
      // research (surfaced as a gentle toast), never undoing the create.
      if (wantResearch) {
        setResearching(true);
        try {
          await onResearch!(created);
          toast({
            tone: 'success',
            title: 'Research attached',
            description: 'Knowledge Base research added to the task.',
          });
        } catch (e) {
          toast({
            tone: 'error',
            title: 'Research unavailable',
            description:
              e instanceof Error
                ? e.message
                : 'Could not reach the knowledge base — the task was still created.',
          });
        } finally {
          setResearching(false);
        }
      }

      onClose();
      onCreated(created, estimateNow);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Create failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title="Create task"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            {researching
              ? 'Researching…'
              : estimateNow
                ? 'Create & estimate'
                : researchNow && onResearch
                  ? 'Create & research'
                  : 'Create task'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          placeholder="Implement token refresh"
          onChange={(e) => setTitle(e.target.value)}
          hint="Just a title to start — add the rest later."
          autoFocus
        />
        <Textarea
          label="Description (optional)"
          value={description}
          rows={3}
          placeholder="Context, acceptance criteria, links… (optional)"
          onChange={(e) => setDescription(e.target.value)}
          hint="Optional — also used to ground Vector KB research below."
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Type"
            value={type}
            options={TYPE_OPTIONS}
            onChange={(e) => setType(e.target.value as TaskType)}
          />
          <Select
            label="Sprint"
            value={sprintChoice}
            options={sprintOptions}
            onChange={(e) => setSprintChoice(e.target.value as SprintChoice)}
          />
        </div>

        {/* Estimate now toggle */}
        <button
          type="button"
          onClick={() => setEstimateNow((v) => !v)}
          aria-pressed={estimateNow}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
            estimateNow
              ? 'border-accent bg-accent-soft/30'
              : 'border-surface-border bg-surface-1 hover:border-zinc-600',
          )}
        >
          <span
            className={cn(
              'relative h-5 w-9 shrink-0 rounded-full transition-colors',
              estimateNow ? 'bg-accent' : 'bg-surface-3',
            )}
            aria-hidden="true"
          >
            <span
              className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                estimateNow ? 'translate-x-4' : 'translate-x-0.5',
              )}
            />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
              <Sparkles className="h-3.5 w-3.5 text-accent-fg" aria-hidden="true" />
              Estimate now
            </span>
            <span className="mt-0.5 block text-xs text-zinc-500">
              Open the estimate flow right after creating this task.
            </span>
          </span>
        </button>

        {/* Research with Vector KB toggle (opt-in) */}
        {onResearch && (
          <button
            type="button"
            onClick={() => setResearchNow((v) => !v)}
            aria-pressed={researchNow}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
              researchNow
                ? 'border-accent bg-accent-soft/30'
                : 'border-surface-border bg-surface-1 hover:border-zinc-600',
            )}
          >
            <span
              className={cn(
                'relative h-5 w-9 shrink-0 rounded-full transition-colors',
                researchNow ? 'bg-accent' : 'bg-surface-3',
              )}
              aria-hidden="true"
            >
              <span
                className={cn(
                  'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                  researchNow ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
                <Library className="h-3.5 w-3.5 text-accent-fg" aria-hidden="true" />
                Research with Vector KB
              </span>
              <span className="mt-0.5 block text-xs text-zinc-500">
                Ground the task's title + description against the team knowledge
                base and attach the AI research to it.
              </span>
            </span>
          </button>
        )}
      </div>
    </Modal>
  );
}

export default QuickCreateTaskModal;
